import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enTranslations from "./locales/en/translation.json";
import esTranslations from "./locales/es/translation.json";

const resources = {
  en: {
    translation: enTranslations,
  },
  es: {
    translation: esTranslations,
  },
};

/**
 * Gets locale from URL pathname
 * Handles paths like /user/es/login or /es/login
 */
const getLocaleFromUrl = (pathname: string): string | null => {
  // Remove /user/ prefix if present
  const withoutBase = pathname.replace(/^\/user\//, "");
  const pathSegments = withoutBase.split("/").filter(Boolean);
  
  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0];
    if (firstSegment === "en" || firstSegment === "es") {
      return firstSegment;
    }
  }
  
  return null;
};

/**
 * Gets stored locale from localStorage, checking both lnc-i18n and i18nextLng
 * Prioritizes lnc-i18n if it exists, otherwise falls back to i18nextLng
 */
const getStoredLocale = (): string => {
  // Check lnc-i18n first (priority)
  const lincLocale = localStorage.getItem("lnc-i18n");
  if (lincLocale && (lincLocale === "en" || lincLocale === "es")) {
    return lincLocale;
  }
  
  // Fall back to i18nextLng
  const i18nextLocale = localStorage.getItem("i18nextLng");
  if (i18nextLocale && (i18nextLocale === "en" || i18nextLocale === "es")) {
    return i18nextLocale;
  }
  
  return "en";
};

/**
 * Gets the initial locale with priority: URL > localStorage > default
 * This ensures URL locale has highest priority when page loads (e.g., when URL is copied to new browser)
 */
const getInitialLocale = (): string => {
  // Priority 1: URL locale (highest priority)
  // Check window.location.pathname if available (browser environment)
  if (typeof window !== "undefined" && window.location) {
    const urlLocale = getLocaleFromUrl(window.location.pathname);
    if (urlLocale) {
      // Update localStorage to match URL locale
      localStorage.setItem("i18nextLng", urlLocale);
      localStorage.setItem("lnc-i18n", urlLocale);
      return urlLocale;
    }
  }
  
  // Priority 2: localStorage (lnc-i18n or i18nextLng)
  const storedLocale = getStoredLocale();
  
  // Priority 3: Default locale
  return storedLocale;
};

// Initialize with priority: URL > localStorage > default
const initialLocale = getInitialLocale();
// Ensure both keys are set with the initial locale
localStorage.setItem("i18nextLng", initialLocale);
localStorage.setItem("lnc-i18n", initialLocale);

// impactomics-common-layout is a sibling file: package whose dist bundle
// contains a synchronous i18n.init() side-effect (src/i18n/config.ts).
// Because ES modules are evaluated depth-first, that init fires *before*
// this module body runs — making our own i18n.init() a no-op.
//
// Strategy:
//   • If i18n is not yet initialized → normal init path (wins the race).
//   • If i18n was already initialized by the package → skip init and use
//     addResourceBundle (deep + overwrite) to merge our app translations on
//     top. The package's own keys (common.english, mainLayout.*, etc.) are
//     preserved for keys we don't override.
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      lng: initialLocale,
      fallbackLng: "en",
      supportedLngs: ["en", "es"],
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: "i18nextLng",
        caches: ["localStorage"],
      },
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    });
} else {
  // Deep-merge: host-app keys are added; conflicts are overwritten by ours.
  i18n.addResourceBundle("en", "translation", enTranslations, true, true);
  i18n.addResourceBundle("es", "translation", esTranslations, true, true);

  if (i18n.language !== initialLocale) {
    i18n.changeLanguage(initialLocale);
  }
}

// Listen for language changes and sync both localStorage keys
i18n.on("languageChanged", (lng: string) => {
  if (lng === "en" || lng === "es") {
    localStorage.setItem("i18nextLng", lng);
    localStorage.setItem("lnc-i18n", lng);
  }
});

export default i18n;

